import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { communityService } from '../api/services';
import { Loader2, AlertCircle, PhoneOff, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const CommunityCallPage = () => {
  const { id }        = useParams();
  const { user }      = useAuth();
  const navigate      = useNavigate();
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [callData,    setCallData]    = useState(null);
  const [community,   setCommunity]   = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        const [communityRes, tokenRes] = await Promise.all([
          communityService.getById(id),
          communityService.getCallToken(id),
        ]);
        const c  = communityRes.data?.data?.community ?? communityRes.data?.community;
        const td = tokenRes.data?.data ?? tokenRes.data;
        setCommunity(c);
        setCallData(td);
      } catch (err) {
        setError(err.response?.data?.message || 'Could not start call');
      } finally { setLoading(false); }
    };
    init();
  }, [id]);

  if (loading) return (
    <div className="min-h-screen bg-forest-950 flex items-center justify-center">
      <div className="text-center space-y-3">
        <Loader2 size={36} className="animate-spin text-forest-400 mx-auto" />
        <p className="text-forest-400 text-sm">Starting community call...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-forest-950 flex items-center justify-center px-4">
      <div className="text-center space-y-4">
        <AlertCircle size={40} className="mx-auto text-rust-400" />
        <h3 className="font-serif text-xl text-cream-50">Could not start call</h3>
        <p className="text-sm text-forest-400">{error}</p>
        <button onClick={() => navigate(`/communities/${id}`)} className="btn-primary">
          Back to Community
        </button>
      </div>
    </div>
  );

  const roomUrl = callData?.token
    ? `${callData.roomUrl}?t=${callData.token}`
    : callData?.roomUrl;

  return (
    <div className="min-h-screen bg-forest-950 flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3 bg-forest-900 border-b border-forest-800 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg overflow-hidden flex-shrink-0">
            {community?.imageUrl
              ? <img src={community.imageUrl} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full bg-forest-700 flex items-center justify-center">
                  <span className="text-white text-[10px] font-bold">{community?.name?.[0]}</span>
                </div>
            }
          </div>
          <div>
            <span className="text-sm font-medium text-cream-50">{community?.name}</span>
            <div className="flex items-center gap-1.5 text-xs text-forest-400">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              <span>Community Call</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-xs text-forest-400">
            <Users size={12} />
            {community?.members?.length ?? 0} members
          </span>
          <button
            onClick={() => navigate(`/communities/${id}`)}
            className="flex items-center gap-1.5 text-xs font-semibold bg-rust-500 hover:bg-rust-600 text-white px-4 py-2 rounded-full transition-colors">
            <PhoneOff size={13} /> Leave Call
          </button>
        </div>
      </div>

      {/* Daily.co room */}
      <div className="flex-1">
        {roomUrl ? (
          <iframe
            src={roomUrl}
            allow="camera; microphone; fullscreen; display-capture; autoplay"
            className="w-full h-full border-0"
            style={{ minHeight: 'calc(100vh - 53px)' }}
            title={`${community?.name} Community Call`}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <Loader2 size={32} className="animate-spin text-forest-400" />
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityCallPage;